import { useMutation } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';

type TrackingParams = {
  userAgent: string;
  eventType: string;
  fbc?: string;
  ttclid?: string;
  platform?: 'facebook' | 'tiktok';
};

import { Loader } from '../../components/_atoms/_Loader';
import { Center } from '../../components/_atoms/Center';
import { NextPageWithLayout } from '../../components/layouts';
import { useMetrics } from '../../hooks/useMetrics';
import BackendService from '../../services/backendService/backendService';
import {
  convertFbclidToFbc,
  getFbclid,
  getTtclid,
  getUserAgent,
} from '../../utils/adsConversionTrackingUtils';
import { getBaseUrl, isProduction } from '../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { clearPostAuthRedirect } from '../../utils/postAuthRedirect';

/**
 * Post-signup landing page. It tracks the signup conversion (Facebook/TikTok
 * conversion APIs) and then redirects the user on to their destination.
 */
const SignupSuccess: NextPageWithLayout = () => {
  const router = useRouter();
  const ref = useRef<NodeJS.Timeout | undefined>(undefined);
  const { data: session } = useSession();
  // Unauthenticated logger: the session may still be settling when we fire.
  const metrics = useMetrics(true);
  // Fire the signup-success event at most once per landing (guards against the
  // effect re-running before the redirect unmounts the page).
  const signupTracked = useRef(false);

  // Use mutation for conversion tracking (both Facebook and TikTok)
  const { mutate: trackConversion } = useMutation({
    mutationFn: BackendService.instance.marketing.trackConversion,
    onError: error => {
      console.error('Error tracking conversion:', error);
    },
  });

  useEffect(() => {
    // A failed signup (OAuth provisioning returned no session) lands here with an
    // error session and no idToken — otherwise we'd sit on the loader forever
    // waiting for a token that never arrives. Route to the styled error page with
    // the reason + support reference. BackendServiceProvider also clears the
    // session globally; this guarantees the /auth/error experience regardless of
    // that timing.
    if (session?.error === 'ProvisioningError') {
      // Use the current origin (matching BackendServiceProvider) so previews /
      // misconfigured NEXT_PUBLIC_BASE_URL don't redirect to the wrong host.
      const url = new URL(`${window.location.origin}/auth/error`);
      url.searchParams.set('error_type', session.authErrorType ?? 'oauthCallbackError');
      if (session.correlationId)
        url.searchParams.set('correlation_id', session.correlationId);
      if (!isProduction()) url.searchParams.set('newauth', '1');
      window.location.replace(url.toString());
      return;
    }

    // Wait for session to be loaded before tracking conversion
    if (!session?.idToken) {
      return;
    }

    // A genuine, non-errored session reached the success landing — the signup
    // completed. This page is the shared post-signup destination for BOTH the
    // every signup flow (via buildSignupSuccessCallback), so
    // it's the single reliable place to count a successful signup in Mixpanel.
    if (!signupTracked.current) {
      signupTracked.current = true;
      metrics.logEvent('Signup.Success', { isNewUser: !!session.isNewUser });
    }

    // Get tracking parameters from URL or localStorage (e.g. if callback URL was stripped)
    const fbclid = getFbclid();
    const ttclid = getTtclid();

    // Track conversion if any tracking parameter is present
    if (fbclid || ttclid) {
      const userAgent = getUserAgent();

      const trackingParams: TrackingParams = {
        userAgent,
        eventType: 'CompleteRegistration',
      };

      if (fbclid) {
        trackingParams.fbc = convertFbclidToFbc(fbclid);
        trackingParams.platform = 'facebook';
      }

      if (ttclid) {
        trackingParams.ttclid = ttclid;
        trackingParams.platform = 'tiktok';
      }

      // If both are present, prioritize Facebook for now (can be changed based on business logic)
      if (fbclid && ttclid) {
        trackingParams.platform = 'facebook';
      }

      trackConversion(trackingParams);
    }

    if (router) {
      const callbackURI = router.query.callbackURI as string | undefined;
      if (callbackURI) {
        router.replace(callbackURI);
      } else {
        router.replace('/');
      }
      clearPostAuthRedirect();
      return;
    }
    // A timeout that makes sure the user is not stuck on this site
    if (!ref.current) {
      ref.current = setTimeout(() => {
        window.location.replace(`${getBaseUrl()}/`);
      }, 10000);
    }
    return () => {
      if (ref.current) clearTimeout(ref.current);
    };
  }, [router, trackConversion, session, metrics]);
  return (
    <Center style={{ width: '100%', height: '100%' }}>
      <Loader />
    </Center>
  );
};
export const getServerSideProps: GetServerSideProps = async context => {
  // No caching: this is a per-user post-signup landing that tracks a conversion +
  // signup event and then redirects. A cached copy could mis-track or skip the
  // signup event for a later user, so it must run fresh every time.
  context.res.setHeader(
    'Cache-Control',
    'no-store, no-cache, must-revalidate, proxy-revalidate',
  );
  return {
    props: {
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
    },
  };
};
export default SignupSuccess;
